/**
 * Forces a browser "Save as" download for any URL, including cross-origin ones.
 *
 * Setting {@code <a download>} alone only works for same-origin URLs — for S3/MinIO
 * (or other CDN) links the browser ignores {@code download} and navigates to the
 * resource, which opens images/PDFs inline. Fetching the bytes first and creating a
 * blob object URL makes {@code download} work for every content type.
 *
 * Presentation presigned URLs also set {@code Content-Disposition: attachment}. If the
 * blob fetch fails (CORS, network), we fall back to a hidden iframe navigation so the
 * browser still saves the file via that header — without leaving the page.
 */
export async function forceFileDownload(url: string, fileName?: string | null): Promise<void> {
  const name = (fileName && fileName.trim()) || 'presentation'
  const fetchUrl = toSameOriginPath(url)

  try {
    const res = await fetch(fetchUrl)
    if (!res.ok) {
      throw new Error(`Download failed (${res.status})`)
    }
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    clickDownloadAnchor(objectUrl, name)
    // Immediate revoke cancels in-flight downloads in Chrome/Safari; keep the blob
    // alive long enough for the browser to start reading it.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  } catch {
    // Fallback: Content-Disposition: attachment on presentation URLs. Prefer iframe
    // over <a download> — after an async gap, programmatic anchor clicks are often
    // ignored, and cross-origin download attrs are ignored entirely.
    triggerAttachmentViaIframe(url)
  }
}

/** Prefer a same-origin relative path so fetch stays first-party (cookies / proxy). */
function toSameOriginPath(url: string): string {
  try {
    const parsed = new URL(url, window.location.href)
    if (parsed.origin === window.location.origin) {
      return `${parsed.pathname}${parsed.search}`
    }
  } catch {
    /* keep absolute */
  }
  return url
}

function clickDownloadAnchor(href: string, fileName: string): void {
  const a = document.createElement('a')
  a.href = href
  a.download = fileName
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function triggerAttachmentViaIframe(url: string): void {
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url
  document.body.appendChild(iframe)
  window.setTimeout(() => iframe.remove(), 60_000)
}
